import React, { useEffect, useState } from "react";
import "./addProject.css";
import { UilCheck, UilImageUpload, UilPlus } from "@iconscout/react-unicons";
import { useNavigate, useParams } from "react-router";
import { toast } from "react-toastify";
import axios from "axios";
import { Tooltip } from "react-tooltip";

const EditProject = () => {
  const [name, setName] = useState("");
  const [arName, setArName] = useState("");
  const [category, setCategory] = useState("");
  const [arCategory, setArCategory] = useState("");
  const [description, setDescription] = useState("");
  const [arDescription, setArDescription] = useState("");
  const [image, setImage] = useState(null);
  const [secondaryImages, setSecondaryImages] = useState([]);
  const [alt, setAlt] = useState("");
  const [arAlt, setArAlt] = useState("");
  const [error, setError] = useState("");
  const [, setLoading] = useState(false);
  const token =
    localStorage.getItem("authToken") || sessionStorage.getItem("authToken");

  const navigate = useNavigate();
  const { id } = useParams();

  useEffect(() => {
    if (!token) {
      navigate("/dashboard/login");
    }
  }, [token, navigate]);

  const convertSecondaryImagesToArray = (images) => {
    if (Array.isArray(images)) {
      return images;
    } else if (typeof images === "object" && images !== null) {
      return Object.values(images).filter((img) => img !== null); 
    }
    return []; 
  };

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const response = await axios.get(`/projects/${id}`);
        const data = response.data;
        setName(data.name || "");
        setArName(data.arName || "");
        setCategory(data.category || "");
        setArCategory(data.arCategory || "");
        setDescription(data.description || "");
        setArDescription(data.arDescription || "");
        setImage(data.image?.url || null);
        setAlt(data.image?.alt || "");
        setArAlt(data.image?.arAlt || "");

        const secondaryImagesArray = convertSecondaryImagesToArray(
          data.secondaryImages
        );
        console.log("Secondary Images Array:", secondaryImagesArray);
        setSecondaryImages(secondaryImagesArray);
      } catch (error) {
        toast.error("Error fetching project data.");
      }
    };

    if (id) {
      fetchProject();
    }
  }, [id, token]);

  const handleFileUpload = (file) => {
    if (file && file.type.startsWith("image/")) {
      setImage(file);
      setError("");
    } else {
      setError("Please upload a valid image file (png, jpg, jpeg).");
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    handleFileUpload(file);
  };

  const handleRemoveImage = () => {
    setImage(null);
  };

  const handleSecondryImagesUpload = (file, index) => {
    const newSecondaryImages = [...secondaryImages];
    newSecondaryImages[index] = file;
    setSecondaryImages(newSecondaryImages);
  };

  const handleRemoveSecondryImages = (index) => {
    const newSecondaryImages = [...secondaryImages];
    newSecondaryImages.splice(index, 1);
    setSecondaryImages(newSecondaryImages);
  };

  const handleAddSecondaryImage = () => {
    setSecondaryImages([...secondaryImages, null]);
  };

  const imageFormats = [
    "jpg",
    "jpeg",
    "png",
    "gif",
    "bmp",
    "tiff",
    "webp",
    "svg",
    "heic",
    "ico",
    "avif",
  ];

  const handleEditProject = async (e) => {
    e.preventDefault();

    const projectData = new FormData();
    projectData.append("name", name);
    projectData.append("arName", arName);
    projectData.append("category", category);
    projectData.append("arCategory", arCategory);
    projectData.append("description", description);
    projectData.append("arDescription", arDescription);
    projectData.append("image", image);
    projectData.append("alt", alt);
    projectData.append("arAlt", arAlt);

    secondaryImages.forEach((img) => {
      if (img) {
        projectData.append("secondaryImages", img);
      }
    });

    try {
      setLoading(true);
      await toast.promise(
        axios.put(`/projects/${id}`, projectData, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }),
        {
          pending: "Editing project...",
          success: "Project edited successfully!",
        }
      );

      navigate("/dashboard/projects");
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="addProject">
      <form className="container" onSubmit={handleEditProject}>
        <div className="title">
          <div className="text">
            <p>Edit Project</p>
          </div>
          <button>
            <UilCheck size={22} />
            Edit Project
          </button>
        </div>
        <div className="form">
          <div className="inputs">
            <h3>General information</h3>
            <div className="input">
              <label>
                Name <span>*</span>
              </label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                type="text"
                placeholder="Give your project a short and clear name."
              />
            </div>
            <div className="input ar-input">
              <label>
                الاسم <span>*</span>
              </label>
              <input
                value={arName}
                onChange={(e) => setArName(e.target.value)}
                type="text"
                placeholder="امنح مشروعك اسمًا قصيرًا وواضحًا."
              />
            </div>
            <hr />
            <div className="input">
              <label>
                Category <span>*</span>
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value="" hidden>
                  Choose your project category.
                </option>
                <option value="Work Space">Work Space</option>
                <option value="Education">Education</option>
                <option value="Residential">Residential</option>
                <option value="Entertainment">Entertainment</option>
                <option value="Commercial">Commercial</option>
                <option value="Hospitality">Hospitality</option>
              </select>
            </div>
            <div className="input ar-input">
              <label>
                الفئة <span>*</span>
              </label>
              <select
                value={arCategory}
                onChange={(e) => setArCategory(e.target.value)}
              >
                <option value="" hidden>
                  اختر فئة مشروعك.
                </option>
                <option value="مساحة العمل">مساحة العمل</option>
                <option value="تعليم">تعليم</option>
                <option value="سكني">سكني</option>
                <option value="ترفيه">ترفيه</option>
                <option value="تجاري">تجاري</option>
                <option value="ضيافة">ضيافة</option>
              </select>
            </div>
            <hr />
            <div className="input textarea">
              <label>
                Description <span>*</span>
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Give your project a short and clear description."
              ></textarea>
            </div>
            <div className="input textarea ar-input">
              <label>
                الوصف <span>*</span>
              </label>
              <textarea
                value={arDescription}
                onChange={(e) => setArDescription(e.target.value)}
                placeholder="امنح مشروعك وصفًا قصيرًا وواضحًا."
              ></textarea>
            </div>
          </div>

          <div className="image">
            <h3>Upload image</h3>
            {image ? (
              <div className="imgPreview">
                <img
                  src={
                    typeof image === "object" && image instanceof File
                      ? URL.createObjectURL(image)
                      : image
                  }
                  alt="Uploaded"
                />
                <button className="remove-btn" onClick={handleRemoveImage}>
                  <i className="uil uil-times"></i>
                </button>
              </div>
            ) : (
              <div
                className="imgInput"
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDrop}
              >
                <UilImageUpload size={40} />
                <p>Drop your main image here, or</p>
                <div className="btn">
                  <button type="button">Click here</button>
                  <input
                    type="file"
                    onChange={(e) => handleFileUpload(e.target.files[0])}
                    accept="image/*"
                  />
                </div>
              </div>
            )}

            {error && <p className="error">{error}</p>}

            <div className="secondary-images">
              {secondaryImages.map((img, index) => (
                <div
                  key={index}
                  className="image-container"
                  style={{ width: "100%" }}
                >
                  {img ? (
                    <div className="imgPreview">
                      <img
                        src={
                          typeof img === "object" && img instanceof File
                            ? URL.createObjectURL(img)
                            : img
                        }
                        alt={`Uploaded ${index}`}
                      />
                      <button
                        className="remove-btn"
                        onClick={() => handleRemoveSecondryImages(index)}
                      >
                        <i className="uil uil-times"></i>
                      </button>
                    </div>
                  ) : (
                    <div
                      className="imgInput"
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        handleSecondryImagesUpload(
                          e.dataTransfer.files[0],
                          index
                        );
                      }}
                    >
                      <UilImageUpload size={24} />
                      <div className="btn">
                        <button type="button">Click</button>
                        <input
                          type="file"
                          onChange={(e) =>
                            handleSecondryImagesUpload(e.target.files[0], index)
                          }
                          accept="image/*"
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {secondaryImages.length < 100 && (
                <div
                  className="imgInput add-more"
                  onClick={handleAddSecondaryImage}
                >
                  <UilPlus size={24} />
                  <p>Add more</p>
                </div>
              )}
            </div>

            {!image && (
              <>
                <div className="image-p">
                  <p className="inf">Upload your project images.</p>
                  <i className="uil uil-info-circle" id="formats"></i>
                </div>
                <Tooltip
                  anchorSelect="#formats"
                  content={imageFormats?.join(", ")}
                  place="top"
                />
              </>
            )}

            <div className="input">
              <label>
                Image alt <span>*</span>
              </label>
              <input
                value={alt}
                onChange={(e) => setAlt(e.target.value)}
                type="text"
                placeholder="Give your project image a short alt text."
              />
            </div>
            <div className="input ar-input">
              <label>
                نص الصورة البديل <span>*</span>
              </label>
              <input
                value={arAlt}
                onChange={(e) => setArAlt(e.target.value)}
                type="text"
                placeholder="أعط صورة مشروعك نصًا بديلًا قصيرًا."
              />
            </div>
          </div>
        </div>
      </form>
    </main>
  );
};

export default EditProject;
